import React from 'react';
import {
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconDots, IconPlus, IconX } from '@tabler/icons';
import { formatDistanceToNow, parseJSON } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useAuth, useAccount } from '../../auth';
import {
  useAccountUsers,
  useAccountInvitations,
  useRevokeInvitation,
} from '../../data/useAccountUsers';
import type { AccountUser, Invitation } from '../../data/useAccountUsers';
import InviteUserDialog from './InviteUserDialog';
import RemoveUserDialog from './RemoveUserDialog';
import ChangeRoleDialog from './ChangeRoleDialog';
import AQDataGrid from '../AQDataGrid';

const ROLE_LABELS: Record<string, string> = {
  viewer: 'Viewer',
  creator: 'Creator',
  'account-admin': 'Admin',
};

const ROLE_COLORS: Record<string, 'default' | 'primary' | 'warning'> = {
  viewer: 'default',
  creator: 'primary',
  'account-admin': 'warning',
};

const AdminUsers: React.FC = () => {
  const { user: authUser } = useAuth();
  const { currentAccount } = useAccount();
  const accountId = currentAccount?.id;

  const usersQuery = useAccountUsers(accountId);
  const invitationsQuery = useAccountInvitations(accountId);
  const revokeInvitation = useRevokeInvitation(accountId);

  const TAB_NAMES = ['members', 'invitations'] as const;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab =
    tabParam && TAB_NAMES.includes(tabParam as any)
      ? TAB_NAMES.indexOf(tabParam as (typeof TAB_NAMES)[number])
      : 0;
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [removeTarget, setRemoveTarget] = React.useState<{
    userId: string;
    displayName: string;
  } | null>(null);
  const [roleTarget, setRoleTarget] = React.useState<{
    userId: string;
    displayName: string;
    currentRole: string;
  } | null>(null);

  // Menu anchor for per-row actions
  const [menuAnchor, setMenuAnchor] = React.useState<{
    el: HTMLElement;
    user: AccountUser;
  } | null>(null);

  const users = usersQuery.data?.data || [];
  const invitations = invitationsQuery.data?.data || [];

  const currentUserId = authUser?.roles?.find(
    (r: any) => r.account.id === accountId,
  )
    ? String(
        (authUser as any).id ||
          authUser?.roles?.find((r: any) => r.account.id === accountId)
            ?.user_id,
      )
    : undefined;

  const handleRevoke = (tokenId: string) => {
    revokeInvitation.mutate(tokenId);
  };

  const pendingCount = invitations.length;

  // Transform users into flat rows for AQDataGrid
  const memberRows = React.useMemo(
    () =>
      users.map((user) => ({
        id: user.id,
        display_name: user.attributes.display_name || '-',
        email: user.attributes.email,
        role: user.attributes.role,
        isCurrentUser: user.id === currentUserId,
        _raw: user,
      })),
    [users, currentUserId],
  );

  // Transform invitations into flat rows for AQDataGrid
  const invitationRows = React.useMemo(
    () =>
      invitations.map((inv) => ({
        id: inv.id,
        email: inv.attributes.email,
        role: inv.attributes.role,
        created_at: inv.attributes.created_at,
        _raw: inv,
      })),
    [invitations],
  );

  // Column definitions for members table
  const memberColDefs = React.useMemo(
    () => [
      {
        field: 'display_name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {params.value}
            {params.row.isCurrentUser && (
              <Typography
                component="span"
                variant="caption"
                sx={{ color: 'text.secondary' }}
              >
                (you)
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 220,
        sortable: true,
        filterable: true,
      },
      {
        field: 'role',
        headerName: 'Role',
        width: 140,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Chip
            label={ROLE_LABELS[params.value] || params.value}
            size="small"
            color={ROLE_COLORS[params.value] || 'default'}
            variant="outlined"
            onClick={() =>
              setRoleTarget({
                userId: params.row.id,
                displayName: params.row.display_name,
                currentRole: params.value,
              })
            }
            sx={{ cursor: 'pointer' }}
          />
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        renderCell: (params: any) =>
          !params.row.isCurrentUser ? (
            <IconButton
              size="small"
              onClick={(e) =>
                setMenuAnchor({ el: e.currentTarget, user: params.row._raw })
              }
            >
              <IconDots size={16} />
            </IconButton>
          ) : null,
      },
    ],
    [],
  );

  // Column definitions for invitations table
  const invitationColDefs = React.useMemo(
    () => [
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 220,
        sortable: true,
        filterable: true,
      },
      {
        field: 'role',
        headerName: 'Role',
        width: 140,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Chip
            label={ROLE_LABELS[params.value] || params.value}
            size="small"
            color={ROLE_COLORS[params.value] || 'default'}
            variant="outlined"
          />
        ),
      },
      {
        field: 'created_at',
        headerName: 'Sent',
        width: 180,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Typography variant="body2" color="text.secondary">
            {formatDistanceToNow(parseJSON(params.value), {
              addSuffix: true,
            })}
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => (
          <Tooltip title="Revoke invitation">
            <IconButton
              size="small"
              onClick={() => handleRevoke(params.row.id)}
              disabled={revokeInvitation.isPending}
            >
              <IconX size={16} />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [revokeInvitation.isPending],
  );

  const gridProps = React.useMemo(
    () => ({
      getEstimatedRowHeight: () => 52,
      getRowHeight: () => 'auto' as const,
      sortingMode: 'client' as const,
      filterMode: 'client' as const,
      paginationMode: 'client' as const,
      disableColumnMenu: true,
      disableRowSelectionOnClick: true,
    }),
    [],
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          maxWidth: 1200,
          margin: '0 auto 24px',
        }}
      >
        <Typography variant="h5">Users</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<IconPlus size={16} />}
          onClick={() => setInviteOpen(true)}
        >
          Invite User
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setSearchParams({ tab: TAB_NAMES[v] })}
        sx={{
          mb: 2,
          borderBottom: 1,
          borderColor: 'divider',
          maxWidth: 1200,
          margin: '0 auto',
        }}
      >
        <Tab label="Members" />
        <Tab
          sx={{ overflow: 'visible' }}
          label={
            <Badge
              badgeContent={pendingCount}
              color="primary"
              sx={{ '& .MuiBadge-badge': { right: -12, top: -2 } }}
            >
              Invitations
            </Badge>
          }
        />
      </Tabs>

      {/* Members tab */}
      {tab === 0 && (
        <AQDataGrid
          id="admin-members"
          rows={memberRows}
          colDefs={memberColDefs}
          loading={usersQuery.isLoading}
          gridProps={gridProps}
          customErrorElement={
            usersQuery.isError ? (
              <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
                Failed to load users
              </Typography>
            ) : undefined
          }
          sx={{
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
              py: '12px',
            },
          }}
        />
      )}

      {/* Invitations tab */}
      {tab === 1 && (
        <AQDataGrid
          id="admin-invitations"
          rows={invitationRows}
          colDefs={invitationColDefs}
          loading={invitationsQuery.isLoading}
          gridProps={gridProps}
          customErrorElement={
            invitationsQuery.isError ? (
              <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
                Failed to load invitations
              </Typography>
            ) : undefined
          }
          sx={{
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
              py: '12px',
            },
          }}
        />
      )}

      {/* Row actions menu (Members) */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            if (menuAnchor) {
              setRemoveTarget({
                userId: menuAnchor.user.id,
                displayName:
                  menuAnchor.user.attributes.display_name ||
                  menuAnchor.user.attributes.email,
              });
            }
            setMenuAnchor(null);
          }}
          sx={{ color: 'error.main' }}
        >
          Remove user
        </MenuItem>
      </Menu>

      {/* Change role dialog */}
      {roleTarget && (
        <ChangeRoleDialog
          open={Boolean(roleTarget)}
          onClose={() => setRoleTarget(null)}
          accountId={accountId}
          userId={roleTarget.userId}
          displayName={roleTarget.displayName}
          currentRole={roleTarget.currentRole}
        />
      )}

      {/* Invite dialog */}
      <InviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        accountId={accountId}
      />

      {/* Remove dialog */}
      {removeTarget && (
        <RemoveUserDialog
          open={Boolean(removeTarget)}
          onClose={() => setRemoveTarget(null)}
          accountId={accountId}
          userId={removeTarget.userId}
          displayName={removeTarget.displayName}
        />
      )}
    </Box>
  );
};

export default AdminUsers;
